
import { useState } from 'react';

type PhaseStatus = 'completed' | 'in-progress' | 'pending' | 'delayed';

interface Task {
  name: string;
  department: string;
  start: number;
  duration: number;
  status: PhaseStatus;
  milestone?: string;
  detail: string;
}

interface Phase {
  id: string;
  title: string;
  icon: string;
  color: string;
  period: string;
  tasks: Task[];
}

const statusConfig: Record<PhaseStatus, { label: string; bg: string; text: string; dot: string }> = {
  completed: { label: '완료', bg: 'bg-teal-100', text: 'text-teal-700', dot: 'bg-teal-500' },
  'in-progress': { label: '진행중', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  pending: { label: '예정', bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-300' },
  delayed: { label: '지연', bg: 'bg-rose-100', text: 'text-rose-600', dot: 'bg-rose-500' },
};

const months = [
  '25.01', '25.02', '25.03', '25.04', '25.05', '25.06',
  '25.07', '25.08', '25.09', '25.10', '25.11', '25.12',
  '26.01', '26.02', '26.03', '26.04', '26.05', '26.06',
  '26.07', '26.08', '26.09', '26.10', '26.11', '26.12',
];

const phases: Phase[] = [
  {
    id: 'phase1',
    title: '사전 검토 및 토지 정비',
    icon: 'ri-search-eye-line',
    color: 'teal',
    period: '2025.01 ~ 2025.06',
    tasks: [
      { name: '토지이용계획 확인원 발급', department: '법무팀', start: 0, duration: 1, status: 'completed', detail: '8개 필지 토지이용계획 확인 및 용도지역 검증' },
      { name: '토양오염 정밀조사 (1302)', department: '환경팀', start: 0, duration: 2, status: 'completed', detail: '주유소 부지 유류 저장탱크 토양오염도 정밀 분석' },
      { name: '합필 등기 신청 및 처리', department: '법무팀', start: 1, duration: 3, status: 'in-progress', milestone: '합필 완료', detail: '8개 필지 물리적 합필 및 소유권 단일화 등기' },
      { name: '집합건물 대지권 해체 협의', department: '법무팀', start: 1, duration: 4, status: 'in-progress', detail: '1302-1번지 구분소유자 대지권 해체 및 매도청구 절차' },
      { name: '토양정화 이행계획 수립', department: '환경팀', start: 2, duration: 2, status: 'pending', detail: '오염토양 반출 및 정화 공법 선정, 비용 산출' },
      { name: '교통영향평가 사전협의', department: '설계팀', start: 3, duration: 3, status: 'pending', detail: '서초구청 교통과 사전 협의 및 평가 범위 확정' },
    ],
  },
  {
    id: 'phase2',
    title: '인허가 핵심 절차',
    icon: 'ri-file-list-3-line',
    color: 'amber',
    period: '2025.04 ~ 2025.12',
    tasks: [
      { name: '건축심의 사전검토 신청', department: '설계팀', start: 3, duration: 2, status: 'pending', detail: '서초구 건축위원회 사전검토 신청 및 설계안 제출' },
      { name: '환경영향평가서 작성·제출', department: '환경팀', start: 4, duration: 4, status: 'pending', milestone: '환경영향평가 승인', detail: '소음, 대기질, 일조권 등 환경영향 종합 평가' },
      { name: '교통영향평가서 제출', department: '설계팀', start: 5, duration: 3, status: 'pending', detail: '교통량 분석, 주차 수급 계획, 진출입 동선 설계' },
      { name: '건축위원회 심의', department: '설계팀', start: 6, duration: 2, status: 'pending', milestone: '건축심의 통과', detail: '건축 디자인, 공개공지, 공공기여 시설 심의' },
      { name: '공공기여 시설 협의 (3-4F)', department: '사업팀', start: 7, duration: 3, status: 'pending', detail: '서초구청 공공시설 운영 조건 및 용적률 인센티브 협의' },
      { name: '건축허가 신청 및 취득', department: '법무팀', start: 9, duration: 3, status: 'pending', milestone: '건축허가 취득', detail: '건축법 기준 적합성 최종 검토 및 허가 취득' },
    ],
  },
  {
    id: 'phase3',
    title: '착공 전 법적 정비',
    icon: 'ri-hammer-line',
    color: 'emerald',
    period: '2025.10 ~ 2026.04',
    tasks: [
      { name: '사업계획 승인 신청', department: '사업팀', start: 9, duration: 3, status: 'pending', detail: '주택법에 따른 오피스텔 사업계획 승인 절차' },
      { name: '토양정화 완료 확인', department: '환경팀', start: 9, duration: 4, status: 'pending', milestone: '토양정화 완료', detail: '1302번지 토양오염 정화 완료 및 적합 판정' },
      { name: '분양 신고 및 승인', department: '사업팀', start: 12, duration: 2, status: 'pending', detail: '오피스텔 분양 신고서 제출 및 분양가 심사' },
      { name: '착공 신고', department: '설계팀', start: 13, duration: 1, status: 'pending', milestone: '착공', detail: '건축허가 조건 이행 확인 후 착공 신고' },
      { name: '시공사 도급계약 체결', department: '사업팀', start: 12, duration: 2, status: 'pending', detail: '책임준공 확약 포함 도급계약 및 PF 연계' },
    ],
  },
  {
    id: 'phase4',
    title: '시공 중 법적 관리',
    icon: 'ri-building-2-line',
    color: 'rose',
    period: '2026.04 ~ 2028.06',
    tasks: [
      { name: '정기 안전점검 (분기별)', department: '설계팀', start: 14, duration: 8, status: 'pending', detail: '건설기술진흥법에 따른 분기별 정기 안전점검 시행' },
      { name: '중간검사 (구조체 완료)', department: '설계팀', start: 18, duration: 1, status: 'pending', milestone: '구조체 완료', detail: '건축법 제25조에 따른 중간검사 (골조 완료 시점)' },
      { name: '소방시설 완공검사', department: '설계팀', start: 20, duration: 2, status: 'pending', detail: '소방시설 설치 완료 후 소방서 완공검사' },
      { name: '사용승인 (준공검사)', department: '법무팀', start: 22, duration: 2, status: 'pending', milestone: '사용승인 취득', detail: '건축물 사용승인 및 준공검사 완료' },
    ],
  },
];

const milestones = phases.flatMap((phase) =>
  phase.tasks
    .filter((task) => task.milestone)
    .map((task) => ({
      name: task.milestone!,
      month: task.start + task.duration - 1,
      color: phase.color,
    }))
);

const summaryStats = [
  { label: '총 인허가 항목', value: '21', icon: 'ri-file-list-3-line', color: 'teal' },
  { label: '완료 항목', value: '2', icon: 'ri-check-double-line', color: 'emerald' },
  { label: '진행중 항목', value: '2', icon: 'ri-loader-4-line', color: 'amber' },
  { label: '핵심 마일스톤', value: '8', icon: 'ri-flag-line', color: 'rose' },
];

export default function PermitSchedule() {
  const [activePhase, setActivePhase] = useState<string | null>(null);
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);

  const togglePhase = (id: string) => {
    setActivePhase(activePhase === id ? null : id);
  };

  const getBarColor = (status: PhaseStatus, phaseColor: string) => {
    if (status === 'completed') return 'bg-teal-500';
    if (status === 'in-progress') return 'bg-amber-400';
    if (status === 'delayed') return 'bg-rose-400';
    const colorMap: Record<string, string> = {
      teal: 'bg-teal-200',
      amber: 'bg-amber-200',
      emerald: 'bg-emerald-200',
      rose: 'bg-rose-200',
    };
    return colorMap[phaseColor] || 'bg-gray-200';
  };

  const getBarGradient = (status: PhaseStatus, phaseColor: string) => {
    if (status === 'completed') return 'from-teal-400 to-teal-600';
    if (status === 'in-progress') return 'from-amber-300 to-amber-500';
    if (status === 'delayed') return 'from-rose-300 to-rose-500';
    const colorMap: Record<string, string> = {
      teal: 'from-teal-100 to-teal-300',
      amber: 'from-amber-100 to-amber-300',
      emerald: 'from-emerald-100 to-emerald-300',
      rose: 'from-rose-100 to-rose-300',
    };
    return colorMap[phaseColor] || 'from-gray-100 to-gray-300';
  };

  const phaseColorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
    teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
    amber: { bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
    emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
    rose: { bg: 'bg-rose-500', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  };

  return (
    <section id="permit-schedule" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-calendar-check-line text-base"></i>
            PERMIT &amp; LEGAL SCHEDULE
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">인허가 및 법적 검토 일정표</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            사전 검토부터 사용승인까지, 프로젝트의 법적 절차와 인허가 일정을<br />
            단계별로 체계적으로 관리합니다.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {summaryStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center group hover:shadow-lg transition-all cursor-default">
              <div className={`w-14 h-14 bg-${stat.color}-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-${stat.color}-600 transition-colors`}>
                <i className={`${stat.icon} text-2xl text-${stat.color}-600 group-hover:text-white transition-colors`}></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          {Object.entries(statusConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${config.dot}`}></div>
              <span className="text-sm text-gray-600 font-medium">{config.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <i className="ri-flag-line text-sm text-rose-500"></i>
            <span className="text-sm text-gray-600 font-medium">마일스톤</span>
          </div>
        </div>

        {/* Milestone Timeline */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-10">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-flag-line text-teal-600"></i>
            핵심 마일스톤 타임라인
          </h3>
          <div className="relative">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200"></div>
            <div className="flex justify-between relative">
              {milestones.map((ms, index) => {
                const leftPercent = (ms.month / (months.length - 1)) * 100;
                return (
                  <div
                    key={index}
                    className="absolute flex flex-col items-center"
                    style={{ left: `${leftPercent}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className={`w-4 h-4 rounded-full bg-${ms.color}-500 border-4 border-white shadow-md z-10`}></div>
                    <div className={`mt-3 px-3 py-1.5 bg-${ms.color}-50 border border-${ms.color}-200 rounded-lg`}>
                      <div className={`text-xs font-bold text-${ms.color}-700 whitespace-nowrap`}>{ms.name}</div>
                      <div className="text-xs text-gray-400 text-center mt-0.5">{months[ms.month]}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="h-24"></div>
          </div>
        </div>

        {/* Gantt Chart Phases */}
        <div className="space-y-4">
          {phases.map((phase) => {
            const colors = phaseColorMap[phase.color];
            const isOpen = activePhase === phase.id;
            const completedCount = phase.tasks.filter((t) => t.status === 'completed').length;
            const progressPercent = Math.round((completedCount / phase.tasks.length) * 100);

            return (
              <div key={phase.id} className={`bg-white rounded-2xl shadow-sm border ${isOpen ? colors.border : 'border-gray-100'} overflow-hidden transition-all`}>
                {/* Phase Header */}
                <button
                  onClick={() => togglePhase(phase.id)}
                  className="w-full flex items-center justify-between px-8 py-6 cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex items-center gap-5">
                    <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center`}>
                      <i className={`${phase.icon} text-2xl ${colors.text}`}></i>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900">{phase.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{phase.period}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    {/* Progress */}
                    <div className="hidden md:flex items-center gap-3">
                      <div className="w-32 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className={`h-full ${colors.bg} rounded-full transition-all duration-500`} style={{ width: `${progressPercent}%` }}></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">{completedCount}/{phase.tasks.length}</span>
                    </div>
                    {/* Task Status Badges */}
                    <div className="hidden lg:flex items-center gap-2">
                      {phase.tasks.some((t) => t.status === 'completed') && (
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          {phase.tasks.filter((t) => t.status === 'completed').length} 완료
                        </span>
                      )}
                      {phase.tasks.some((t) => t.status === 'in-progress') && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          {phase.tasks.filter((t) => t.status === 'in-progress').length} 진행중
                        </span>
                      )}
                    </div>
                    <i className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {/* Phase Content - Gantt Chart */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {/* Month Headers */}
                    <div className="flex">
                      <div className="w-72 flex-shrink-0 px-8 py-3 bg-gray-50 border-r border-gray-100">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">작업 항목</span>
                      </div>
                      <div className="flex-1 overflow-x-auto">
                        <div className="flex min-w-[960px]">
                          {months.map((month, idx) => (
                            <div
                              key={idx}
                              className={`flex-1 text-center py-3 text-xs font-medium border-r border-gray-50 ${
                                idx === 3 ? 'bg-teal-50/50 text-teal-700 font-bold' : 'text-gray-400 bg-gray-50'
                              }`}
                            >
                              {month}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Task Rows */}
                    {phase.tasks.map((task, taskIdx) => {
                      const taskKey = `${phase.id}-${taskIdx}`;
                      const isHovered = hoveredTask === taskKey;
                      const statusCfg = statusConfig[task.status];

                      return (
                        <div
                          key={taskIdx}
                          className={`flex border-t border-gray-50 transition-colors ${isHovered ? 'bg-gray-50/80' : ''}`}
                          onMouseEnter={() => setHoveredTask(taskKey)}
                          onMouseLeave={() => setHoveredTask(null)}
                        >
                          {/* Task Name */}
                          <div className="w-72 flex-shrink-0 px-8 py-4 border-r border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusCfg.dot}`}></div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">{task.name}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 ${statusCfg.bg} ${statusCfg.text} text-xs font-medium rounded`}>
                                    {statusCfg.label}
                                  </span>
                                  <span className="text-xs text-gray-400">{task.department}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Gantt Bar */}
                          <div className="flex-1 overflow-x-auto">
                            <div className="relative min-w-[960px] h-full">
                              {/* Grid Lines */}
                              <div className="absolute inset-0 flex">
                                {months.map((_, idx) => (
                                  <div key={idx} className="flex-1 border-r border-gray-50"></div>
                                ))}
                              </div>

                              {/* Bar */}
                              <div className="absolute inset-y-0 flex items-center" style={{ left: `${(task.start / months.length) * 100}%`, width: `${(task.duration / months.length) * 100}%` }}>
                                <div className="relative w-full group/bar cursor-pointer">
                                  <div className={`h-8 rounded-lg bg-gradient-to-r ${getBarGradient(task.status, phase.color)} shadow-sm hover:shadow-md transition-all hover:scale-y-110`}>
                                    {task.status === 'in-progress' && (
                                      <div className="absolute inset-0 rounded-lg overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-white/0 to-white/30 animate-pulse"></div>
                                      </div>
                                    )}
                                    {task.milestone && (
                                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                                        <i className={`ri-flag-fill text-sm text-${phase.color}-700`}></i>
                                      </div>
                                    )}
                                  </div>

                                  {/* Tooltip */}
                                  {isHovered && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 pointer-events-none">
                                      <div className="bg-gray-900 text-white rounded-xl px-4 py-3 shadow-xl min-w-[240px]">
                                        <div className="text-sm font-bold mb-1">{task.name}</div>
                                        <div className="text-xs text-gray-300 mb-2">{task.detail}</div>
                                        <div className="flex items-center gap-3 text-xs">
                                          <span className="text-gray-400">
                                            <i className="ri-calendar-line mr-1"></i>
                                            {months[task.start]} ~ {months[Math.min(task.start + task.duration - 1, months.length - 1)]}
                                          </span>
                                          <span className="text-gray-400">
                                            <i className="ri-team-line mr-1"></i>
                                            {task.department}
                                          </span>
                                        </div>
                                        {task.milestone && (
                                          <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-amber-400">
                                            <i className="ri-flag-line mr-1"></i>
                                            마일스톤: {task.milestone}
                                          </div>
                                        )}
                                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900"></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Key Legal Checkpoints */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: 'ri-scales-3-line',
              title: '집합건물법 재건축 결의',
              desc: '1302-1번지 구분소유자 4/5 이상 동의 확보 필요. 매도청구권 행사를 위한 법적 토대 구축이 선행되어야 합니다.',
              tag: '최우선 과제',
              tagColor: 'rose',
            },
            {
              icon: 'ri-leaf-line',
              title: '토양오염 정화 인증',
              desc: '주유소 부지(1302) 지하 유류탱크 제거 및 토양정화 완료 후 환경부 적합 판정을 받아야 착공이 가능합니다.',
              tag: '환경 리스크',
              tagColor: 'amber',
            },
            {
              icon: 'ri-government-line',
              title: '공공기여 시설 협의',
              desc: '3-4층 공공시설 운영 조건 및 용적률 인센티브 협의. 1302-12번지 공공보행통로 전환으로 추가 인센티브 확보 가능.',
              tag: '인센티브 핵심',
              tagColor: 'teal',
            },
            {
              icon: 'ri-building-line',
              title: '건축위원회 심의',
              desc: '필로티 공개공지, 건물 높이, 일조권, 경관 등 종합 심의. 서초구 건축위원회 사전검토를 통해 리스크를 최소화합니다.',
              tag: '설계 핵심',
              tagColor: 'emerald',
            },
            {
              icon: 'ri-car-line',
              title: '교통영향평가 승인',
              desc: '500대 이상 주차장 건설에 따른 교통량 분석 및 진출입 동선 설계. 강남3구 교통 개선 기여도를 적극 어필합니다.',
              tag: '교통 인프라',
              tagColor: 'teal',
            },
            {
              icon: 'ri-shield-check-line',
              title: '사용승인 및 준공',
              desc: '건축물 사용승인, 소방 완공검사, 에너지 효율 등급 인증 등 최종 법적 절차를 완료하여 입주를 개시합니다.',
              tag: '최종 단계',
              tagColor: 'emerald',
            },
          ].map((item, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all cursor-default group">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 bg-${item.tagColor}-50 rounded-xl flex items-center justify-center group-hover:bg-${item.tagColor}-600 transition-colors`}>
                  <i className={`${item.icon} text-2xl text-${item.tagColor}-600 group-hover:text-white transition-colors`}></i>
                </div>
                <span className={`px-3 py-1 bg-${item.tagColor}-50 text-${item.tagColor}-700 text-xs font-bold rounded-full whitespace-nowrap`}>
                  {item.tag}
                </span>
              </div>
              <h4 className="text-base font-bold text-gray-900 mb-2">{item.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Risk Warning */}
        <div className="mt-12 bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-2xl p-8 border border-amber-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-2xl text-amber-700"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">인허가 리스크 관리 유의사항</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <i className="ri-alert-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>집합건물 대지권 해체 지연 시 전체 일정 <strong>최대 6개월</strong> 지연 가능</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-alert-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>토양오염 정화 범위 확대 시 추가 비용 <strong>30~50억원</strong> 발생 가능</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-alert-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>건축심의 조건부 승인 시 설계 변경에 따른 <strong>2~3개월</strong> 추가 소요</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-alert-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>환경영향평가 보완 요청 시 <strong>재심의 3개월</strong> 추가 소요 가능</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
